'use client';

import { forwardRef, useState, useCallback, useImperativeHandle, useEffect, type ReactElement } from 'react';
import { togglePullToRefresh } from '~/services/app-interface';

type OverlayControlRef = {
  close: () => void;
};

export type CreateOverlayElement = (props: { isOpen: boolean; close: () => void; exit?: () => void }) => ReactElement;

export type OverlayControllerOptions = {
  /** PTR 활성화 여부 */
  allowsPTR?: boolean;
  /** overlay 아래 요소 (body) 스크롤 여부 */
  allowsScroll?: boolean;
};

type Props = {
  overlayElement: CreateOverlayElement;
  onExit: () => void;
} & OverlayControllerOptions;

/** Overlay 로 사용할 컴포넌트의 기본적인 기능을 선언화한다. */
export const OverlayController = forwardRef<OverlayControlRef, Props>(function OverlayController(
  { overlayElement: OverlayElement, onExit, allowsPTR = false, allowsScroll = false },
  forwardedRef,
) {
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    togglePullToRefresh(true);
    document.body.style.overflow = 'unset';
    setIsOpen(false);
  }, []);

  useImperativeHandle(
    forwardedRef,
    () => {
      return {
        close: handleClose,
      };
    },
    [handleClose],
  );

  useEffect(() => {
    requestAnimationFrame(() => {
      togglePullToRefresh(allowsPTR);
      document.body.style.overflow = allowsPTR ? 'unset' : allowsScroll ? 'unset' : 'hidden';

      setIsOpen(true);
    });
  }, [allowsPTR, allowsScroll]);

  return <OverlayElement isOpen={isOpen} close={handleClose} exit={onExit} />;
});
